/**
 * Manual Checkout page.
 * (2026-04-16, Jason Cox) New for manual RFID checkout via R6 hand scanner.
 * Smart-only -- SWS users are blocked (see isBlockedSwsUser). See
 * documentation/Manual_Checkout_Feature.md in the rebound-smart repo for context.
 *
 * (2026-05-19, Jason Cox) Added a two-layer guard against double-checkouts.
 * Layer 1: any scanned tag whose lookup returned canCheckout=false is shown
 *   in a persistent warning banner with an explicit reason and BLOCKS the
 *   submit button entirely (no silent filtering). Tags currently being
 *   looked up are excluded from "ineligible" -- only tags whose lookup has
 *   actually returned are counted, otherwise a transient "Loading..." row
 *   would flicker as a block.
 * Layer 2: recentCheckouts -- a localStorage-backed map of rfid -> timestamp
 *   for the last ~10 minutes. Re-adding a tag inside that window is refused
 *   even if a fresh server lookup returns canCheckout=true (defense against
 *   the backend's md_rfid.status flip not yet being visible to a follow-up
 *   lookupTags). Reason: we observed two delivery tickets get created for
 *   the same tag 25 seconds apart in prod (TXAM batch, 2026-05-18), which
 *   the per-tag canCheckout filter alone did not prevent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import RfidScannerPanel from '../components/RfidScannerPanel';
// (2026-04-17, Jason Cox) Camera-based QR/barcode scanner -- adds a third
// input path alongside the BLE hand scanner and manual EPC entry.
import QRScannerModal from '../components/QRScannerModal';
// (2026-08-03) Single-value camera scanner -- used to scan a Work Order barcode
// into the field on the workOrder form variant (fallback to the R6's barcode).
import SingleTagQRScannerModal from '../components/SingleTagQRScannerModal';
// (2026-06-11, Jason Cox) Drawn-signature capture replaces the typed-initials field.
import SignaturePad from '../components/SignaturePad';
// (2026-08-03) Per-company Manual Checkout form variant (see config.json).
import { configService } from '../services/configService';
// (2026-08-04) Read R6 connection state so the Work Order "Scan" button uses
// the hand scanner when connected and only falls back to the camera otherwise.
import { useRfidScannerContext } from '../contexts/RfidScannerContext';
// (2026-07-28) On-device signature retention safety net — keeps a local copy of
// each submitted signature keyed by docNum so it survives a failed server-side
// upload and can be recovered later. State + store/export plumbing live in the
// useSignatureExport hook; see also utils/signatureStore.ts + exportSignatures.ts.
import { useSignatureExport } from '../hooks/useSignatureExport';
import { SignatureCell } from '../components/SignatureCell';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedApi } from '../hooks/useUnifiedApi';
import {
  ManualCheckoutHistoryRow,
  ManualCheckoutTagInfo,
} from '../services/unifiedApiService';
import {
  CameraIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type TabKey = 'checkout' | 'history';

interface ScannedTag extends Partial<ManualCheckoutTagInfo> {
  rfid: string;
}

/**
 * (2026-05-19, Jason Cox) Local memory of recent successful checkouts.
 * Layer 2 of the double-checkout guard. The backend updates md_rfid.status
 * to 5 on checkout, but a follow-up lookupTags call shortly after may still
 * see status=3 (ONHAND) if the row's update hasn't propagated yet -- which
 * is how the TXAM batch (2026-05-18) ended up with two delivery tickets for
 * the same tag 25 seconds apart. Holding a client-side memory of "we just
 * checked this out in this browser" closes that race independently of the
 * server's view.
 */
type RecentCheckoutMap = { [rfid: string]: number };
const RECENT_CHECKOUT_TTL_MS = 10 * 60 * 1000;
const RECENT_CHECKOUT_KEY = 'manualCheckout.recent';
// (2026-08-10) How long the Work Order field stays armed to capture the next R6
// barcode read after the user taps Scan. Once it lapses, barcode reads go back to
// the tag batch. Long enough to walk to the work order, short enough that a
// forgotten tap doesn't silently eat the next tag scan.
const WORK_ORDER_SCAN_WINDOW_MS = 20 * 1000;
/**
 * (2026-08-10) Maintenance Document barcodes are "MD-" + six digits, e.g.
 * MD-507068. (The Maintenance Document number is distinct from the Work Order
 * number despite this field accepting either.)
 *
 * Matching the document shape -- rather than trying to recognize a tag EPC --
 * is deliberate. Tag EPCs are overwhelmingly 12 hex chars (937k of ~939k tags)
 * but run 5-30 chars in the wild, so EPC-shape detection would misfire; no tag
 * in the fleet matches this pattern. Anything unrecognized therefore falls
 * through to the tag batch, which is the safe default: a misrouted tag scan is
 * invisible, while a misrouted document number is obvious on screen.
 */
const MAINTENANCE_DOC_PATTERN = /^MD-\d{6}$/i;

/**
 * (2026-08-10) Backend status vocabulary -> crew-facing wording.
 *
 * lookupTags returns the literal 'NOT_FOUND' for any code it doesn't recognize
 * (see Rfid.php::lookupTags). To someone holding a scanner that reads like a
 * system failure, when it just means the code isn't a tag we know about --
 * usually a stray barcode that isn't a tag label at all.
 *
 * Mapped at render time only. The API value is deliberately left untouched so
 * eligibility logic and any other consumer keep working off the contract.
 */
function displayStatusName(statusName?: string): string {
  if (statusName === 'NOT_FOUND') return 'Not in system';
  return statusName || '';
}

/**
 * Read the recent-checkout map from localStorage and prune any entries
 * older than RECENT_CHECKOUT_TTL_MS. Returns {} on any parse/access error.
 */
function loadRecentCheckouts(): RecentCheckoutMap {
  try {
    const raw = window.localStorage.getItem(RECENT_CHECKOUT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as RecentCheckoutMap;
    const now = Date.now();
    const fresh: RecentCheckoutMap = {};
    Object.entries(parsed).forEach(([rfid, ts]) => {
      // Reason: stale entries past the TTL get dropped so the lockout doesn't
      // become permanent for a tag legitimately returned + re-checked-out later.
      if (typeof ts === 'number' && now - ts < RECENT_CHECKOUT_TTL_MS) {
        fresh[rfid] = ts;
      }
    });
    return fresh;
  } catch {
    return {};
  }
}

/** Persist the recent-checkout map. Swallows errors (private mode, quota, etc.). */
function saveRecentCheckouts(map: RecentCheckoutMap): void {
  try {
    window.localStorage.setItem(RECENT_CHECKOUT_KEY, JSON.stringify(map));
  } catch {
    // Reason: localStorage can throw in private browsing or when quota is
    // exceeded. In-memory state still works as Layer 2 within the page life;
    // we just lose persistence across reloads in that environment.
  }
}

const ManualCheckout: React.FC = () => {
  const { user } = useAuth();
  const api = useUnifiedApi();

  // Manual Checkout is a Smart-only feature. SWS users get bounced to the dashboard.
  // (2026-08-04) Vendor accounts (companyType === 2) are also blocked -- only
  // customer (and platform) accounts check out. Computed up-front; the redirect
  // return happens after all hooks to satisfy rules-of-hooks.
  const isBlockedSwsUser = !!user && user.system !== 'smart';
  const isBlockedVendor = !!user && user.companyType === 2;
  const isBlockedFromCheckout = isBlockedSwsUser || isBlockedVendor;

  const [activeTab, setActiveTab] = useState<TabKey>('checkout');

  // (2026-04-17, Jason Cox) BLE lifecycle moved into RfidScannerPanel / useRfidScanner.

  // Checkout tab state
  const [scannedTags, setScannedTags] = useState<ScannedTag[]>([]);
  const [manualEpc, setManualEpc] = useState('');
  // (2026-04-24, Jason Cox) Associated Party is free-text going forward;
  // the customer dropdown + smartbox-scoped customer list are gone. Billing
  // clientId is derived server-side from the smartbox's workcenter owner.
  const [associatedParty, setAssociatedParty] = useState('');
  // (2026-04-17, Jason Cox) New checkout form fields.
  // (2026-04-22, Jason Cox) Required-field validation is gated on
  // `showValidation`; it flips to true on the first submit attempt so empty
  // required fields get red-bordered + "Required" helper text.
  const [foreman, setForeman] = useState('');
  const [location, setLocation] = useState('');
  // (2026-06-11, Jason Cox) Drawn signature (PNG data URL) replaces the old
  // typed-initials field. null = nothing drawn yet.
  const [signature, setSignature] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  // (2026-06-11, Jason Cox) Picker identity is now free-text, not derived from
  // auth. Reason: deployments share a single login across all operators, so the
  // authenticated session no longer identifies who physically picked up the
  // tags. The operator types it in and we send it to the backend (which prefers
  // these values over the session-derived name/company). Starts empty so the
  // shared-account name isn't pre-filled as misleading noise.
  const [pickerName, setPickerName] = useState('');
  const [pickerCompany, setPickerCompany] = useState('');
  // (2026-08-03) Work Order variant: a single Work Order / Maintenance Document
  // Number field (barcode-scannable) that replaces the TXAM fields for certain
  // customers. The variant is chosen per company from config.json.
  const [workOrder, setWorkOrder] = useState('');
  const [showWorkOrderScanner, setShowWorkOrderScanner] = useState(false);
  // (2026-08-10) The R6's barcode reads are normally tag QR labels bound for the
  // tag batch. Registering a barcode listener at all makes RfidScannerContext
  // route EVERY barcode read to it instead of the tag sink, so the Work Order
  // field may only listen while the user has explicitly asked to capture one --
  // armed by the Scan button, disarmed by the first read or by the timeout.
  const [awaitingWorkOrderScan, setAwaitingWorkOrderScan] = useState(false);
  const workOrderScanTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);
  const lookupTimer = useRef<any>(null);
  // (2026-04-17, Jason Cox) Camera QR scanner modal.
  const [showQRScannerModal, setShowQRScannerModal] = useState(false);

  // History tab state
  const [historyRows, setHistoryRows] = useState<ManualCheckoutHistoryRow[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(25);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearchField, setHistorySearchField] = useState<'rfid' | 'sku'>('rfid');
  const [historySearchValue, setHistorySearchValue] = useState('');
  const [historyStart, setHistoryStart] = useState('');
  const [historyEnd, setHistoryEnd] = useState('');

  // (2026-08-03) On-device signature retention/export (recovery safety net).
  // `storedSigCount` reflects how many signatures are retained locally; the
  // operator can export them as {docNum}.png files for the billing-report tool.
  const { storedSigCount, localSignatures, exportingSigs, persistSignature, runExport } =
    useSignatureExport();

  // (2026-08-03) Which checkout form this user's company sees. Default 'txam'
  // shows the full field set; 'workOrder' shows a single Work Order # field
  // (barcode-scannable) and none of the TXAM fields. Config is loaded well
  // before this authed page renders (login itself needs it), so the synchronous
  // lookup is safe.
  const formVariant = useMemo(
    () => configService.getManualCheckoutFormVariant(user?.companyId),
    [user?.companyId],
  );
  const isWorkOrderForm = formVariant === 'workOrder';

  // (2026-08-04) R6 hand-scanner connection + barcode trigger, used by the
  // Work Order "Scan" button.
  const { connected: scannerConnected, trigger: triggerBarcodeScan } = useRfidScannerContext();

  // (2026-08-10) Arm/disarm the Work Order barcode capture window. See the
  // awaitingWorkOrderScan declaration for why this can't just stay on.
  const disarmWorkOrderScan = useCallback(() => {
    if (workOrderScanTimer.current) {
      clearTimeout(workOrderScanTimer.current);
      workOrderScanTimer.current = null;
    }
    setAwaitingWorkOrderScan(false);
  }, []);

  const armWorkOrderScan = useCallback(() => {
    if (workOrderScanTimer.current) clearTimeout(workOrderScanTimer.current);
    setAwaitingWorkOrderScan(true);
    workOrderScanTimer.current = setTimeout(() => {
      workOrderScanTimer.current = null;
      setAwaitingWorkOrderScan(false);
    }, WORK_ORDER_SCAN_WINDOW_MS);
  }, []);

  // Don't leave a timer running after the page unmounts.
  useEffect(() => () => {
    if (workOrderScanTimer.current) clearTimeout(workOrderScanTimer.current);
  }, []);

  const eligibleCount = useMemo(
    () => scannedTags.filter((t) => t.canCheckout).length,
    [scannedTags]
  );

  // (2026-05-19, Jason Cox) Layer 1 of the double-checkout guard.
  // Surface every scanned tag whose server-side lookup came back ineligible
  // and force the user to remove it (or wait for lookup) before submit.
  // We exclude the transient "Loading..." rows so freshly-added tags don't
  // flash a misleading block while their lookup is in flight.
  const ineligibleTags = useMemo(
    () =>
      scannedTags.filter(
        (t) => !t.canCheckout && (t.statusName ?? '') !== 'Loading...',
      ),
    [scannedTags],
  );

  // (2026-05-19, Jason Cox) Layer 2 of the double-checkout guard. Persistent
  // across page reloads via localStorage so a refresh can't bypass the lock.
  // Pruned at load time; new entries land in handleCheckout on success.
  const [recentCheckouts, setRecentCheckouts] = useState<RecentCheckoutMap>(loadRecentCheckouts);
  useEffect(() => {
    saveRecentCheckouts(recentCheckouts);
  }, [recentCheckouts]);

  /**
   * Build a human-readable reason for an ineligible tag. Special-cases the
   * "already checked out" condition (md_rfid.status === 5) since that is the
   * single most likely reason a manual checkout is rejected, and is what
   * triggered this guard in the first place.
   */
  const explainIneligible = useCallback((t: ScannedTag): string => {
    if (t.status === 5) return 'already checked out — cannot be checked out again';
    // (2026-08-10) "not eligible (Not in system)" reads oddly; say what it means.
    if (t.statusName === 'NOT_FOUND') return 'not a recognized tag';
    if (t.statusName) return `not eligible (${t.statusName})`;
    return 'not eligible for checkout';
  }, []);

  // (2026-04-20, Jason Cox) Derive the scanned batch's smartbox context.
  //   distinctSmartboxIds -- the unique smartboxIds across eligible tags
  //                          (ignoring tags that haven't looked up yet).
  //   activeSmartboxId    -- the single smartbox this batch targets, or null
  //                          if the batch is empty / mixed / still loading.
  //   hasMixedSmartboxes  -- true when tags span >1 smartbox; blocks checkout.
  const distinctSmartboxIds = useMemo(() => {
    const ids = new Set<number>();
    scannedTags.forEach((t) => {
      if (typeof t.smartboxId === 'number' && t.smartboxId > 0) ids.add(t.smartboxId);
    });
    return Array.from(ids);
  }, [scannedTags]);
  const activeSmartboxId = distinctSmartboxIds.length === 1 ? distinctSmartboxIds[0] : null;
  const hasMixedSmartboxes = distinctSmartboxIds.length > 1;

  // --- Tag management ---
  const addTag = useCallback((epc: string) => {
    const clean = (epc || '').trim();
    if (!clean) return;
    // (2026-05-19) Layer 2 of the double-checkout guard: refuse to re-add a
    // tag this browser already checked out within the last 10 minutes, even
    // if a fresh server lookup would now say canCheckout=true. Closes the
    // narrow window where md_rfid.status hasn't propagated yet after a
    // prior successful submit.
    const recentTs = recentCheckouts[clean];
    if (recentTs && Date.now() - recentTs < RECENT_CHECKOUT_TTL_MS) {
      const minsAgo = Math.max(1, Math.round((Date.now() - recentTs) / 60000));
      setMessage({
        kind: 'error',
        text: `Tag ${clean} was already checked out from this browser ${minsAgo} min ago. It cannot be checked out again.`,
      });
      return;
    }
    setScannedTags((prev) => {
      if (prev.find((t) => t.rfid === clean)) {
        setMessage({ kind: 'info', text: `Tag already scanned: ${clean}` });
        return prev;
      }
      const next = [...prev, { rfid: clean, statusName: 'Loading...', canCheckout: false, canCheckin: false }];
      if (lookupTimer.current) clearTimeout(lookupTimer.current);
      lookupTimer.current = setTimeout(() => lookupAll(next.map((t) => t.rfid)), 500);
      return next;
    });
  }, [recentCheckouts]);

  const removeTag = (rfid: string) => {
    setScannedTags((prev) => prev.filter((t) => t.rfid !== rfid));
  };

  const clearTags = () => setScannedTags([]);

  const lookupAll = useCallback(async (rfids: string[]) => {
    if (!api || !api.lookupCheckoutTags || rfids.length === 0) return;
    try {
      const infos = await api.lookupCheckoutTags(rfids);
      setScannedTags((prev) =>
        prev.map((t) => {
          const info = infos.find((i) => i.rfid === t.rfid);
          return info ? { ...t, ...info } : t;
        })
      );
    } catch (err: any) {
      console.error('lookupTags failed:', err);
    }
  }, [api]);

  // --- Checkout submit ---
  const handleCheckout = async () => {
    if (!api || !api.submitManualCheckout) return;
    // (2026-05-19, Jason Cox) Defense-in-depth: block submit if any scanned
    // tag is ineligible. The button is already disabled in this case, but
    // a keyboard / synthetic event path could still call this handler --
    // bail explicitly with an actionable error rather than silently dropping
    // the ineligible tags from the request.
    if (ineligibleTags.length > 0) {
      const summary = ineligibleTags
        .map((t) => `${t.rfid} (${explainIneligible(t)})`)
        .join(', ');
      setMessage({
        kind: 'error',
        text:
          `Cannot check out: ${ineligibleTags.length} tag(s) are not eligible — ${summary}. ` +
          `Remove them from the batch and try again.`,
      });
      return;
    }
    const eligible = scannedTags.filter((t) => t.canCheckout);
    if (eligible.length === 0) {
      setMessage({ kind: 'error', text: 'No eligible tags for checkout' });
      return;
    }
    // (2026-04-22) Validate required fields up front; show red borders + helper
    // text on the specific empty fields rather than dumping everything into the
    // banner. (2026-08-03) The Work Order variant requires only the work-order
    // number; the TXAM variant requires its full field set.
    const missing: string[] = [];
    if (isWorkOrderForm) {
      if (!workOrder.trim()) missing.push('Maintenance Document Number');
    } else {
      if (!associatedParty.trim()) missing.push('Associated Party');
      if (!foreman.trim()) missing.push('Foreman/Approver');
      if (!location.trim()) missing.push('Location');
      if (!pickerName.trim()) missing.push('Who is picking up');
      if (!pickerCompany.trim()) missing.push('Picking-up company');
      if (!signature) missing.push('Signature');
    }
    if (missing.length > 0) {
      setShowValidation(true);
      setMessage({
        kind: 'error',
        text: `Please complete required field${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}`,
      });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      // (2026-08-03) Work Order variant: send ONLY the work-order number, in the
      // associatedParty field (a PWA-only stopgap -- the backend has no
      // dedicated work-order field, and associatedParty is stored as a labeled
      // line on the ticket note and shown in History). All TXAM fields are
      // omitted for this variant.
      // (2026-06-11, Jason Cox) TXAM variant: picker name/company are sent from
      // the form (free-text) rather than derived from the shared login session;
      // the backend prefers these over the session-derived values.
      const res = await api.submitManualCheckout(
        isWorkOrderForm
          ? {
              rfids: eligible.map((t) => t.rfid),
              associatedParty: workOrder.trim(),
            }
          : {
              rfids: eligible.map((t) => t.rfid),
              associatedParty: associatedParty.trim(),
              foreman,
              location,
              pickerName: pickerName.trim(),
              pickerCompany: pickerCompany.trim(),
              // (2026-06-11) Send the drawn signature as a base64 PNG data URL.
              // The backend uploads it to S3 and stores a marker. `signature` is
              // non-null here because validation blocks submit when it's empty.
              signature: signature || undefined,
            },
      );
      const processedRfids = new Set(res.processed.map((p) => p.rfid));
      const successCount = res.processed.length;
      const errorCount = res.errors.length;

      setScannedTags((prev) => prev.filter((t) => !processedRfids.has(t.rfid)));

      // (2026-05-19, Jason Cox) Record processed rfids in the recent-checkout
      // map so re-adding the same tag within the TTL window is refused even
      // if the next server lookup hasn't seen the status flip yet.
      if (res.processed.length > 0) {
        const now = Date.now();
        setRecentCheckouts((prev) => {
          const next = { ...prev };
          res.processed.forEach((p) => {
            next[p.rfid] = now;
          });
          return next;
        });
      }

      // (2026-07-28) Durably retain the drawn signature on-device, keyed by the
      // ticket docNum(s) the backend just assigned, so it can be recovered later
      // even if the server-side S3 upload failed. Best-effort: fully isolated in
      // the hook, never throws, never affects the checkout result.
      await persistSignature(signature, res.processed);

      if (successCount > 0 && errorCount === 0) {
        setMessage({ kind: 'success', text: `${successCount} tag(s) checked out successfully` });
        setAssociatedParty('');
        setForeman('');
        setLocation('');
        setPickerName('');
        setPickerCompany('');
        setSignature(null);
        setWorkOrder('');
        setShowValidation(false);
      } else if (successCount > 0) {
        setMessage({ kind: 'info', text: `${successCount} succeeded, ${errorCount} failed` });
      } else {
        setMessage({ kind: 'error', text: `Checkout failed: ${res.errors[0]?.error || 'unknown'}` });
      }
    } catch (err: any) {
      setMessage({ kind: 'error', text: `Checkout failed: ${err.message || err}` });
    } finally {
      setSubmitting(false);
    }
  };

  // --- History load ---
  const loadHistory = useCallback(async () => {
    if (!api || !api.getManualCheckoutHistory) return;
    setHistoryLoading(true);
    try {
      const res = await api.getManualCheckoutHistory({
        page: historyPage,
        pageSize: historyPageSize,
        rfid: historySearchField === 'rfid' ? historySearchValue : '',
        sku: historySearchField === 'sku' ? historySearchValue : '',
        startTime: historyStart,
        endTime: historyEnd,
      });
      setHistoryRows(res.data);
      setHistoryTotal(res.total);
    } catch (err: any) {
      console.error('manualCheckoutHistory failed:', err);
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, historyPage, historyPageSize, historySearchField, historySearchValue, historyStart, historyEnd]);

  useEffect(() => {
    if (activeTab === 'history' && historyRows.length === 0 && !historyLoading) {
      loadHistory();
    }
  }, [activeTab]);

  /**
   * Export the signatures retained on this device as {docNum}.png files. On
   * Chrome/Edge the operator picks a folder (e.g. the report tool's
   * `signatures/` dir) and we write straight into it; elsewhere each file
   * downloads individually.
   */
  const handleExportSignatures = async () => {
    setMessage(null);
    try {
      const res = await runExport();
      if (res.cancelled) {
        // User dismissed the folder picker — say nothing.
        return;
      }
      if (res.count === 0 && !res.failed) {
        setMessage({ kind: 'info', text: 'No signatures are stored on this device to export.' });
        return;
      }
      if (res.failed && res.count === 0) {
        // Every file failed — surface it as an error.
        setMessage({ kind: 'error', text: `Signature export failed for all ${res.failed} file(s).` });
        return;
      }
      const base =
        res.method === 'directory'
          ? `Exported ${res.count} signature file(s) to the selected folder.`
          : `Downloaded ${res.count} signature file(s).`;
      setMessage({
        // Partial success (some files failed) → info, not success.
        kind: res.failed ? 'info' : 'success',
        text: res.failed ? `${base} ${res.failed} file(s) failed.` : base,
      });
    } catch (err: any) {
      setMessage({ kind: 'error', text: `Signature export failed: ${err?.message || err}` });
    }
    // Note: `exportingSigs` is toggled inside runExport(), so no finally here.
  };

  // --- Scanner actions ---
  // (2026-04-17, Jason Cox) BLE scanner lifecycle now lives in
  // RfidScannerPanel / useRfidScanner. This page just wires addTag() to
  // the panel's onTagScanned callback.

  const addManualTag = () => {
    const val = manualEpc.trim();
    if (val) {
      addTag(val);
      setManualEpc('');
    }
  };

  // Guard: SWS users cannot use Manual Checkout -- bounce to dashboard.
  if (isBlockedFromCheckout) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-secondary-900">Manual Checkout</h2>
          <p className="mt-1 text-sm text-secondary-600">
            Hi {user?.firstName}. Scan tags with the R6 hand scanner (or enter EPCs manually), fill in the checkout details, and submit.
          </p>

          {/* Tabs */}
          <div className="mt-4 border-b border-secondary-200">
            <nav className="-mb-px flex space-x-6">
              <button
                onClick={() => setActiveTab('checkout')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'checkout'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                Checkout
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                History
              </button>
            </nav>
          </div>

          {/* Toast-style message */}
          {message && (
            <div
              className={`mt-4 rounded-md p-3 flex items-start justify-between ${
                message.kind === 'success'
                  ? 'bg-green-50 text-green-800'
                  : message.kind === 'error'
                  ? 'bg-red-50 text-red-800'
                  : 'bg-blue-50 text-blue-800'
              }`}
            >
              <span className="text-sm">{message.text}</span>
              <button onClick={() => setMessage(null)}>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {activeTab === 'checkout' && (
            <div className="mt-4 space-y-4">
              {/* (2026-04-17 / 2026-04-24 / 2026-04-30, Jason Cox) Scanner +
                  Camera stay side-by-side at every width so it's visually
                  clear they're two alternative inputs. The shared intro
                  covers both -- the hand scanner and the camera both read
                  the QR / barcode labels. Both funnel into the same
                  addTag() pipeline. */}
              <p className="text-xs text-secondary-600">
                Scan a QR or barcode label with the RFID hand scanner or your device camera. Multiple tags can be scanned in one session.
              </p>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <RfidScannerPanel
                  title="Hand Scanner"
                  onTagScanned={(epc) => addTag(epc)}
                  // (2026-08-03) On the Work Order variant, route the R6's
                  // barcode reads into the Work Order field instead of the tag
                  // batch. RFID reads still add tags. Omitted otherwise so
                  // barcodes fall back to addTag (unchanged behavior).
                  // (2026-08-10) Registering this at all means the context stops
                  // delivering barcodes to the tag sink, so this handler owns the
                  // routing and must forward tag reads to addTag() itself.
                  // Maintenance Document barcodes (MD-######) fill the field;
                  // everything else is a tag label and goes to the batch.
                  onBarcodeScanned={
                    isWorkOrderForm
                      ? (code) => {
                          const clean = (code || '').trim();
                          if (!clean) return;
                          // An explicit Scan-button capture takes the next read
                          // whatever its shape -- covers reprinted or malformed
                          // document labels the pattern wouldn't recognize.
                          if (awaitingWorkOrderScan) {
                            setWorkOrder(clean);
                            disarmWorkOrderScan();
                            return;
                          }
                          if (MAINTENANCE_DOC_PATTERN.test(clean)) {
                            const doc = clean.toUpperCase();
                            setWorkOrder(doc);
                            setMessage({
                              kind: 'info',
                              text: `Maintenance Document number captured: ${doc}`,
                            });
                            return;
                          }
                          addTag(clean);
                        }
                      : undefined
                  }
                  onError={(text) => setMessage({ kind: 'error', text })}
                />
                <div className="card">
                  <div className="card-body">
                    <h3 className="text-sm font-semibold text-secondary-900 mb-2">Camera</h3>
                    <button
                      type="button"
                      onClick={() => setShowQRScannerModal(true)}
                      className="btn-primary w-full flex flex-col items-center justify-center whitespace-nowrap"
                    >
                      <CameraIcon className="h-5 w-5 mb-1" />
                      <span>Start</span>
                      <span>Camera</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Manual EPC entry (third input path -- keyboard / paste).
                  (2026-04-30) The input was flex-1 min-w-0, which stretched
                  it to fill the row and squeezed the Add Tag / Clear
                  buttons. EPCs are 12 hex characters, so a fixed sub-200px
                  width is plenty -- font-mono gives predictable per-char
                  width and maxLength=12 caps user input. The row now wraps
                  naturally on narrow viewports without the input cannibalizing
                  the button widths. */}
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={manualEpc}
                  onChange={(e) => setManualEpc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addManualTag(); }}
                  placeholder="Enter EPC..."
                  maxLength={12}
                  className="input w-44 max-w-full font-mono uppercase tracking-wider"
                />
                <button onClick={addManualTag} className="btn-secondary">
                  <PlusIcon className="h-4 w-4 inline mr-1" /> Add Tag
                </button>
                {scannedTags.length > 0 && (
                  <button onClick={clearTags} className="btn-secondary text-red-600">
                    <TrashIcon className="h-4 w-4 inline mr-1" /> Clear ({scannedTags.length})
                  </button>
                )}
              </div>

              {/* Scanned tags table */}
              <div className="card overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200 text-sm">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">EPC / RFID</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Item No.</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Description</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">SmartBox</th>
                      <th className="px-4 py-2 text-center font-medium text-secondary-700">Status</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {scannedTags.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-secondary-500">
                          No tags scanned yet. Connect scanner and pull trigger, or enter an EPC manually.
                        </td>
                      </tr>
                    )}
                    {scannedTags.map((t) => (
                      <tr key={t.rfid}>
                        <td className="px-4 py-2 font-mono">{t.rfid}</td>
                        <td className="px-4 py-2">{t.sku || ''}</td>
                        <td className="px-4 py-2">{t.itemName || ''}</td>
                        <td className="px-4 py-2">{t.smartboxName || ''}</td>
                        <td className="px-4 py-2 text-center">
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              t.canCheckout
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {displayStatusName(t.statusName) || '...'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => removeTag(t.rfid)}
                            className="text-red-500 hover:text-red-700"
                            aria-label="Remove"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Checkout form -- (2026-04-17, Jason Cox)
                  Fields: Customer billing-to, Foreman/Approver, Lease/Well/Property,
                  (read-only picker name + company), Initials. Picker identity is
                  derived from the session server-side. */}
              {scannedTags.length > 0 && (
                <div className="card">
                  <div className="card-body space-y-3">
                    {/* (2026-04-20, Jason Cox) Mixed-smartbox batches are blocked
                        for transactional clarity -- each checkout now writes
                        one ticket per tag, but we still want the operator to
                        commit from a single smartbox at a time. */}
                    {hasMixedSmartboxes && (
                      <div className="rounded-md p-3 bg-red-50 text-red-800 text-sm">
                        Scanned tags span {distinctSmartboxIds.length} different smartboxes. Remove tags so the batch is from a single smartbox before checking out.
                      </div>
                    )}
                    {/* (2026-05-19, Jason Cox) Ineligibility banner: names
                        each scanned tag whose server lookup said it is not
                        eligible for checkout and the specific reason. The
                        submit button is disabled while any of these are
                        present -- the user must click the row's X to remove
                        the bad tag before they can submit. */}
                    {ineligibleTags.length > 0 && (
                      <div
                        role="alert"
                        data-testid="ineligible-banner"
                        className="rounded-md p-3 bg-red-50 text-red-800 text-sm"
                      >
                        <p className="font-semibold">
                          {ineligibleTags.length} tag{ineligibleTags.length === 1 ? '' : 's'} cannot be checked out:
                        </p>
                        <ul className="mt-1 list-disc list-inside">
                          {ineligibleTags.map((t) => (
                            <li key={t.rfid}>
                              <span className="font-mono">{t.rfid}</span> — {explainIneligible(t)}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2">
                          Remove {ineligibleTags.length === 1 ? 'it' : 'them'} from the batch (click the X on the row) and try again.
                        </p>
                      </div>
                    )}
                    {isWorkOrderForm ? (
                      /* (2026-08-03) Work Order variant: one barcode-scannable
                         Maintenance Document Number field, no TXAM fields.
                         Populated by the R6 barcode scanner, the camera (Scan
                         button), or manual typing.
                         (2026-08-10) Label narrowed to the Maintenance Document
                         Number -- Profrac doesn't scan or record the Work Order
                         number, which is a genuinely different identifier. The
                         'workOrder' variant key and state name are left alone:
                         "workOrder" is a deployed value in config.json's
                         manualCheckoutForms map. */
                      <div>
                        <label className="block text-xs font-medium text-secondary-700">
                          Maintenance Document Number
                          <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <div className="mt-1 flex gap-2">
                          <input
                            value={workOrder}
                            onChange={(e) => setWorkOrder(e.target.value)}
                            className={`${showValidation && !workOrder.trim() ? 'input-error' : 'input'} w-full`}
                            placeholder="Scan a barcode or type the number"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              // (2026-08-04) Prefer the R6 hand scanner when it's
                              // connected -- trigger a barcode read that flows into
                              // this field via onBarcodeScanned. Only fall back to
                              // the camera when the R6 isn't connected.
                              // (2026-08-10) Arm the capture window first so this
                              // read -- and only this read -- lands in the field.
                              if (scannerConnected) {
                                armWorkOrderScan();
                                triggerBarcodeScan();
                              } else {
                                setShowWorkOrderScanner(true);
                              }
                            }}
                            className="btn-secondary whitespace-nowrap"
                          >
                            Scan
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-secondary-500">
                          Maintenance Document barcodes (MD-000000) fill this in automatically when
                          you scan them with the R6 &mdash; no need to press Scan. Use Scan for the
                          camera, or to capture a label this field doesn&rsquo;t recognize.
                        </p>
                        {showValidation && !workOrder.trim() && (
                          <p className="mt-1 text-xs text-error-600">Required</p>
                        )}
                      </div>
                    ) : (
                      <>
                    {/* (2026-04-22) Required-field styling: red-bordered
                        .input-error + "Required" helper below each empty
                        required input, active only after first submit attempt. */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-secondary-700">
                          Associated Party<span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          value={associatedParty}
                          onChange={(e) => setAssociatedParty(e.target.value)}
                          className={`${showValidation && !associatedParty.trim() ? 'input-error' : 'input'} mt-1 w-full`}
                          placeholder="Customer or vendor name"
                        />
                        {showValidation && !associatedParty.trim() && (
                          <p className="mt-1 text-xs text-error-600">Required</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700">
                          Customer Foreman / Approver<span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          value={foreman}
                          onChange={(e) => setForeman(e.target.value)}
                          className={`${showValidation && !foreman.trim() ? 'input-error' : 'input'} mt-1 w-full`}
                          placeholder="Customer foreman or approver"
                        />
                        {showValidation && !foreman.trim() && (
                          <p className="mt-1 text-xs text-error-600">Required</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700">
                          Location<span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          className={`${showValidation && !location.trim() ? 'input-error' : 'input'} mt-1 w-full`}
                          placeholder="Lease, well, property, or site name"
                        />
                        {showValidation && !location.trim() && (
                          <p className="mt-1 text-xs text-error-600">Required</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* (2026-06-11, Jason Cox) Free-text picker identity --
                          a shared login means the session can't identify the
                          operator, so they type who's picking up and for which
                          company. Sent to the backend and stored on the ticket. */}
                      <div>
                        <label className="block text-xs font-medium text-secondary-700">
                          Who is picking up<span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          value={pickerName}
                          onChange={(e) => setPickerName(e.target.value)}
                          className={`${showValidation && !pickerName.trim() ? 'input-error' : 'input'} mt-1 w-full`}
                          placeholder="Name of person picking up"
                        />
                        {showValidation && !pickerName.trim() && (
                          <p className="mt-1 text-xs text-error-600">Required</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700">
                          Picking-up company<span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          value={pickerCompany}
                          onChange={(e) => setPickerCompany(e.target.value)}
                          className={`${showValidation && !pickerCompany.trim() ? 'input-error' : 'input'} mt-1 w-full`}
                          placeholder="Company the person is picking up for"
                        />
                        {showValidation && !pickerCompany.trim() && (
                          <p className="mt-1 text-xs text-error-600">Required</p>
                        )}
                      </div>
                    </div>
                    {/* (2026-06-11, Jason Cox) Drawn signature replaces typed
                        initials. Full-width so there's room to sign with a
                        finger/stylus. Required-field styling mirrors the inputs
                        above: error border + "Required" helper after first submit. */}
                    <div>
                      <label className="block text-xs font-medium text-secondary-700">
                        Signature<span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <SignaturePad
                        value={signature}
                        onChange={setSignature}
                        error={showValidation && !signature}
                        className="mt-1"
                      />
                      {showValidation && !signature && (
                        <p className="mt-1 text-xs text-error-600">Required</p>
                      )}
                    </div>
                      </>
                    )}
                    <button
                      onClick={handleCheckout}
                      disabled={
                        submitting ||
                        eligibleCount === 0 ||
                        hasMixedSmartboxes ||
                        ineligibleTags.length > 0
                      }
                      className="btn-primary w-full disabled:opacity-50"
                    >
                      {submitting
                        ? 'Checking out...'
                        : hasMixedSmartboxes
                          ? 'Resolve mixed smartboxes to checkout'
                          : ineligibleTags.length > 0
                            ? `Remove ${ineligibleTags.length} ineligible tag${ineligibleTags.length === 1 ? '' : 's'} to checkout`
                            : `Checkout ${eligibleCount} Tag${eligibleCount !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <label className="block text-xs font-medium text-secondary-700">From</label>
                  <input type="date" value={historyStart} onChange={(e) => setHistoryStart(e.target.value)} className="input mt-1" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700">To</label>
                  <input type="date" value={historyEnd} onChange={(e) => setHistoryEnd(e.target.value)} className="input mt-1" />
                </div>
                {/* (2026-04-17, Jason Cox) History search: fixed-width type
                    dropdown + flex-grow input. Initial layout had the input
                    too narrow and the dropdown too wide. */}
                <div className="flex-1 min-w-64 max-w-md">
                  <label className="block text-xs font-medium text-secondary-700">Search</label>
                  <div className="mt-1 flex">
                    <select
                      value={historySearchField}
                      onChange={(e) => setHistorySearchField(e.target.value as 'rfid' | 'sku')}
                      className="input rounded-r-none w-28 flex-none"
                    >
                      <option value="rfid">RFID</option>
                      <option value="sku">Item No.</option>
                    </select>
                    <input
                      value={historySearchValue}
                      onChange={(e) => setHistorySearchValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setHistoryPage(1); loadHistory(); } }}
                      className="input rounded-l-none -ml-px flex-1 min-w-0"
                      placeholder={historySearchField === 'rfid' ? 'Enter RFID...' : 'Enter SKU...'}
                    />
                  </div>
                </div>
                <button onClick={() => { setHistoryPage(1); loadHistory(); }} className="btn-primary">
                  Search
                </button>
                {/* (2026-08-03) Export the signatures retained on this device as
                    {docNum}.png files for the manual-checkout-report tool (used
                    when the server-side S3 image is missing). */}
                <button
                  onClick={handleExportSignatures}
                  disabled={exportingSigs || storedSigCount === 0}
                  className="btn-secondary ml-auto disabled:opacity-50"
                  title="Export signatures stored on this device as {docNum}.png files"
                >
                  {exportingSigs ? 'Exporting…' : `Export Signatures (${storedSigCount})`}
                </button>
              </div>

              <div className="card overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200 text-sm">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Ticket</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">RFID</th>
                      {/* (2026-04-17, Jason Cox) Columns aligned with new field set. */}
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Item No.</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Description</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Customer</th>
                      {/* (2026-04-21 / 2026-04-24, Jason Cox) Associated Party
                          is free-text entered at checkout. Distinct from Customer,
                          which is the smartbox's workcenter owner (the billing
                          target). */}
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Associated Party</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Foreman</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Location</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Picked Up By</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Picker Co.</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Signature</th>
                      <th className="px-4 py-2 text-left font-medium text-secondary-700">Date</th>
                      <th className="px-4 py-2 text-center font-medium text-secondary-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {historyLoading && (
                      <tr>
                        <td colSpan={13} className="px-4 py-6 text-center text-secondary-500">Loading...</td>
                      </tr>
                    )}
                    {!historyLoading && historyRows.length === 0 && (
                      <tr>
                        <td colSpan={13} className="px-4 py-6 text-center text-secondary-500">
                          No manual checkout history found.
                        </td>
                      </tr>
                    )}
                    {!historyLoading && historyRows.map((r, idx) => (
                      <tr key={`${r.docNum}-${r.rfid}-${idx}`}>
                        <td className="px-4 py-2 font-mono text-xs">{r.docNum}</td>
                        <td className="px-4 py-2 font-mono text-xs">{r.rfid}</td>
                        <td className="px-4 py-2">{r.sku}</td>
                        <td className="px-4 py-2">{r.itemName}</td>
                        <td className="px-4 py-2">{r.clientName}</td>
                        <td className="px-4 py-2">{r.associatedParty}</td>
                        <td className="px-4 py-2">{r.foreman}</td>
                        <td className="px-4 py-2">{r.location}</td>
                        <td className="px-4 py-2">{r.pickerName}</td>
                        <td className="px-4 py-2">{r.pickerCompany}</td>
                        {/* (2026-06-11, Jason Cox) Show the drawn signature image
                            when present; fall back to the legacy initials text for
                            tickets created before the signature-image change.
                            (2026-08-13) When the S3 image is missing, fall back to
                            the copy retained on this device so the signature is
                            still visible here. */}
                        <td className="px-4 py-2">
                          <SignatureCell
                            signatureUrl={r.signatureUrl}
                            localSignature={localSignatures.get(r.docNum)}
                            initials={r.initials}
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div>{r.docDate}</div>
                          <div className="text-xs text-secondary-500">{r.docTime}</div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            r.isCheckedOut ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {r.currentStatusName}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-secondary-600">Total: {historyTotal} items</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setHistoryPage((p) => Math.max(1, p - 1)); loadHistory(); }}
                    disabled={historyPage <= 1 || historyLoading}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1">Page {historyPage}</span>
                  <button
                    onClick={() => { setHistoryPage((p) => p + 1); loadHistory(); }}
                    disabled={historyPage * historyPageSize >= historyTotal || historyLoading}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* (2026-04-17, Jason Cox) Camera QR scanner modal. Feeds scanned tags
          into the same addTag() pipeline used by the BLE scanner and manual
          input. QRScannerModal accumulates multiple tags and returns them
          together via onTagsAdded. */}
      <QRScannerModal
        isOpen={showQRScannerModal}
        onClose={() => setShowQRScannerModal(false)}
        onTagsAdded={(tags) => {
          tags.forEach((t) => addTag(t));
          setShowQRScannerModal(false);
        }}
      />

      {/* (2026-08-03) Single-value camera scanner for the Work Order field --
          scans one barcode and drops it into the field (camera alternative to
          the R6's barcode reader). */}
      <SingleTagQRScannerModal
        isOpen={showWorkOrderScanner}
        onClose={() => setShowWorkOrderScanner(false)}
        onTagConfirmed={(code) => {
          setWorkOrder(code.trim());
          setShowWorkOrderScanner(false);
        }}
      />
    </Layout>
  );
};

export default ManualCheckout;
